import type { ReactNode } from 'react';

export interface OrderQrCardProps {
  /**
   * QR slot — QR rendering is the app's job (spec: always ink modules on white).
   * The card guarantees the white background.
   */
  qr: ReactNode;
  /** Item name(s). */
  itemName: string;
  /** Short order code (spec §1.3, 8 chars) — staff can type it if scanning fails. */
  code: string;
  /** SR prefix for the code, e.g. t('ec2.order.code'). */
  codeLabel: string;
  /** t('ec2.order.showAtBooth') — `Show this at the swag booth`. */
  caption: string;
  /** Optional per-event 4-digit order number for verbal lookup. */
  orderNumber?: string;
  className?: string;
}

/**
 * Receipt-ticket order card (brief §6 "Shop + order QR"): white card in both
 * themes (always white behind QRs), ink text, QR, item name, order code in
 * display size. Brightness raising while shown is the app's job.
 */
export function OrderQrCard({ qr, itemName, code, codeLabel, caption, orderNumber, className = '' }: OrderQrCardProps) {
  return (
    <div
      data-testid="ec-order-qr-card"
      className={`mx-auto flex w-full max-w-xs flex-col items-center gap-3 rounded-(--radius-card) bg-white p-6 text-ink shadow-lg ${className}`}
    >
      <div className="flex size-48 items-center justify-center bg-white [&>*]:max-h-full [&>*]:max-w-full">{qr}</div>
      <p className="text-center text-base font-medium">{itemName}</p>
      <p className="text-center font-display text-3xl font-bold tracking-[0.06em] tabular-nums">
        <span className="sr-only">{codeLabel}: </span>
        {code}
      </p>
      {orderNumber && <p className="text-center font-display text-lg font-[650] tabular-nums text-ink/70">#{orderNumber}</p>}
      <p className="text-center text-[13px] font-medium text-ink/60">{caption}</p>
    </div>
  );
}
